import { Slider as BaseSlider } from '@base-ui/react/slider';
import type { ComponentProps, ReactNode } from 'react';
import { cx } from '../lib/styled.tsx';
import { fieldLabel } from '../lib/surfaces.ts';

export interface SliderProps extends ComponentProps<typeof BaseSlider.Root> {
  label?: ReactNode;
  /** Show the formatted value opposite the label. */
  showValue?: boolean;
  className?: string;
}

/**
 * A value picked by dragging. Pass an array as the value to get a range — the
 * number of thumbs follows the number of values, so a two-handled slider needs
 * nothing beyond `defaultValue={[20, 60]}`.
 */
export function Slider({ label, showValue, className, ...rootProps }: SliderProps) {
  const value = rootProps.value ?? rootProps.defaultValue;
  const thumbCount = Array.isArray(value) ? value.length : 1;

  return (
    <BaseSlider.Root className={cx('flex w-full flex-col gap-2', className)} {...rootProps}>
      {label || showValue ? (
        <div className="flex items-baseline justify-between gap-4">
          {label ? <BaseSlider.Label className={fieldLabel}>{label}</BaseSlider.Label> : null}
          {showValue ? <BaseSlider.Value className="text-sm tabular-nums text-ink-muted" /> : null}
        </div>
      ) : null}
      <BaseSlider.Control
        className={cx(
          'flex w-full touch-none items-center py-2 select-none',
          'data-[orientation=vertical]:h-48 data-[orientation=vertical]:w-auto data-[orientation=vertical]:justify-center data-[orientation=vertical]:px-2',
          'data-disabled:cursor-not-allowed data-disabled:opacity-50',
        )}
      >
        <BaseSlider.Track
          className={cx(
            'relative rounded-full bg-border-subtle',
            'data-[orientation=horizontal]:h-[5px] data-[orientation=horizontal]:w-full',
            'data-[orientation=vertical]:h-full data-[orientation=vertical]:w-[5px]',
          )}
        >
          <BaseSlider.Indicator className="rounded-full bg-brand-400" />
          {Array.from({ length: thumbCount }, (_, index) => (
            <BaseSlider.Thumb
              // biome-ignore lint/suspicious/noArrayIndexKey: thumbs are positional and never reorder
              key={index}
              className={cx(
                'size-4 rounded-full bg-surface-raised shadow-knob outline-none',
                'transition-transform duration-150 ease-out-quint data-dragging:scale-110',
                'focus-visible:ring-2 focus-visible:ring-brand-400/35',
              )}
            />
          ))}
        </BaseSlider.Track>
      </BaseSlider.Control>
    </BaseSlider.Root>
  );
}
